import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Zap, Sparkles, ClipboardList, Pencil, CalendarDays, Clock, Search, ChevronDown, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, EmptyState } from "../lib/ui";
import { ORDER_STATUS, MASCOTS, orderOwner, fmtWIB, fmtDateWIB } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import OrderDetailModal from "../components/OrderDetailModal";

const ORDER_TYPE_META = {
  REGULAR: { label: "Regular", tone: "mute", icon: ClipboardList },
  URGENT: { label: "Urgent", tone: "danger", icon: Zap },
  SPECIAL: { label: "Special", tone: "info", icon: Sparkles },
};
const STATUS_FILTERS = ["ALL", "DRAFT", "SUBMITTED", "APPROVED", "PURCHASED", "DELIVERED", "RECEIVED"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function bucketOf(orderDate) {
  const now = new Date();
  const today = ymd(now);
  const yd = new Date(now); yd.setDate(now.getDate() - 1);
  if (!orderDate) return "earlier";
  if (orderDate >= today) return "today";
  if (orderDate === ymd(yd)) return "yesterday";
  return "earlier";
}

// ---- compact, dense operational order row ----
function OrderRow({ o, onOpen, onEdit, canEditDraft, t }) {
  const st = ORDER_STATUS[o.status] || { label: o.status, tone: "mute" };
  const owner = orderOwner(o);
  return (
    <div onClick={onOpen} data-testid={`order-card-${o.order_no}`}
      className="flex cursor-pointer items-center gap-3 rounded-xl border border-line bg-white px-3 py-2.5 transition hover:border-primary/40 hover:bg-app">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="min-w-0 truncate font-heading text-sm font-extrabold text-ink" data-testid={`order-branch-${o.order_no}`}>{o.branch_name || o.order_no}</p>
          {owner && <Badge tone={owner.tone} className="hidden shrink-0 sm:inline-flex">{t(owner.label)}</Badge>}
          {o.order_type && o.order_type !== "REGULAR" && <Badge tone={ORDER_TYPE_META[o.order_type]?.tone} className="hidden shrink-0 sm:inline-flex">{t(ORDER_TYPE_META[o.order_type]?.label)}</Badge>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-mute">
          <span className="inline-flex items-center gap-1"><CalendarDays size={11} className="text-primary" /> {fmtDateWIB(o.requested_delivery_date || o.order_date)}</span>
          <span className="text-line">·</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {o.submitted_at ? fmtWIB(o.submitted_at) : t("Not submitted")}</span>
          <span className="text-line">·</span>
          <span className="font-semibold text-ink-soft">{o.items?.length || 0} {t("items")}</span>
          <span className="text-line">·</span>
          <span className="font-mono">{o.order_no}</span>
        </div>
        {owner && <Badge tone={owner.tone} className="mt-1 inline-flex sm:hidden">{t(owner.label)}</Badge>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Badge tone={st.tone}>{t(st.label)}</Badge>
        {canEditDraft && (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`edit-draft-${o.order_no}`}>
            <Pencil size={13} /> {t("Edit")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, open, onToggle, tone, children, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <button onClick={onToggle} className="flex w-full items-center gap-2 rounded-xl bg-app px-3 py-2 text-left transition hover:bg-stone-100" data-testid={`${testid}-toggle`}>
        {open ? <ChevronDown size={16} className="text-ink-mute" /> : <ChevronRight size={16} className="text-ink-mute" />}
        <span className="font-heading text-sm font-extrabold text-ink">{title}</span>
        <Badge tone={tone || "mute"} className="ml-1">{count}</Badge>
      </button>
      {open && (count > 0 ? <div className="mt-2 space-y-2">{children}</div>
        : <p className="px-3 py-3 text-center text-xs text-ink-mute">{/* empty */}—</p>)}
    </div>
  );
}

function Stat({ label, value, tone, active }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${tone}`} data-testid={`orders-stat-${label}`}>
      <p className="font-heading text-2xl font-extrabold leading-none">{value}</p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide opacity-80">{active}</p>
    </div>
  );
}

export default function Orders() {
  const { user, isRole } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({ today: false, yesterday: true, earlier: true, completed: true });

  const load = () => api.get("/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);
  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (orders || []).filter((o) => {
      if (statusFilter !== "ALL" && o.status !== statusFilter) return false;
      if (!q) return true;
      if ((o.branch_name || "").toLowerCase().includes(q)) return true;
      if ((o.order_no || "").toLowerCase().includes(q)) return true;
      if ((o.items || []).some((it) => (it.product_name || "").toLowerCase().includes(q))) return true;
      return false;
    });
  }, [orders, statusFilter, search]);

  const summary = useMemo(() => {
    const today = ymd(new Date());
    const todays = (orders || []).filter((o) => (o.order_date || "") >= today);
    const c = (fn) => todays.filter(fn).length;
    return {
      total: todays.length,
      waiting: c((o) => o.status === "SUBMITTED"),
      processing: c((o) => ["APPROVED", "PURCHASED", "DELIVERY_ADJUSTMENT"].includes(o.status)),
      delivered: c((o) => o.status === "DELIVERED"),
      completed: c((o) => o.status === "RECEIVED"),
    };
  }, [orders]);

  if (!orders) return <PageLoader />;
  const canCreate = isRole("BRANCH_STAFF", "SUPER_ADMIN");
  const countFor = (s) => (s === "ALL" ? orders.length : orders.filter((o) => o.status === s).length);

  const active = filtered.filter((o) => o.status !== "RECEIVED");
  const completed = filtered.filter((o) => o.status === "RECEIVED");
  const byBucket = { today: [], yesterday: [], earlier: [] };
  active.forEach((o) => byBucket[bucketOf(o.order_date)].push(o));

  const searching = search.trim() !== "" || statusFilter !== "ALL";
  const isOpen = (k) => (searching ? true : !collapsed[k]);

  const row = (o) => (
    <OrderRow key={o.id} o={o} t={t}
      onOpen={() => setViewId(o.id)}
      onEdit={() => navigate(`/orders/${o.id}/edit`)}
      canEditDraft={o.status === "DRAFT" && (isRole("SUPER_ADMIN") || o.created_by === user?.id)} />
  );

  const dateSections = [
    ["today", t("Today")],
    ["yesterday", t("Yesterday")],
    ["earlier", t("Earlier")],
  ];

  return (
    <div className="animate-fade-up pb-20 lg:pb-2">
      <PageHeader title={t("Branch Orders")} subtitle={t("Create, submit and track branch orders")} icon={ShoppingCart}>
        {canCreate && (
          <Button onClick={() => navigate("/orders/new")} data-testid="new-order-button">
            <Plus size={18} /> {t("New Order")}
          </Button>
        )}
      </PageHeader>

      {/* Operational summary — today at a glance */}
      <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="orders-summary">
        <Stat value={summary.total} active={t("Total Today")} tone="border-primary/30 bg-primary-light text-primary" />
        <Stat value={summary.waiting} active={t("Waiting")} tone="border-warn/30 bg-warn-light text-warn-text" />
        <Stat value={summary.processing} active={t("Processing")} tone="border-blue-200 bg-blue-50 text-blue-700" />
        <Stat value={summary.delivered} active={t("Delivered")} tone="border-emerald-200 bg-emerald-50 text-emerald-700" />
        <Stat value={summary.completed} active={t("Completed")} tone="border-line bg-app text-ink-soft" />
      </div>

      {/* Search + quick status filter */}
      <div className="mb-4 space-y-3">
        <div className="relative" data-testid="orders-search-wrap">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search"
            placeholder={t("Search by branch, reference or product…")}
            className="w-full rounded-xl border border-line bg-white py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`status-filter-${s}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-all ${statusFilter === s ? "border-primary bg-primary text-white shadow-sm" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>
              {s === "ALL" ? t("All") : t(ORDER_STATUS[s]?.label || s)}
              <span className={`rounded-full px-1.5 text-[11px] ${statusFilter === s ? "bg-white/25" : "bg-stone-100 text-ink-mute"}`}>{countFor(s)}</span>
            </button>
          ))}
          <span className="ml-auto self-center text-xs font-semibold text-ink-mute" data-testid="orders-result-count">{filtered.length} {t("orders")}</span>
        </div>
      </div>

      {orders.length === 0 ? (
        <EmptyState
          image={MASCOTS.order}
          title={t("No orders yet")}
          subtitle={t("Start by creating a new branch order. It's quick and fun!")}
          action={canCreate && <Button onClick={() => navigate("/orders/new")}><Plus size={18} /> {t("New Order")}</Button>}
        />
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="orders-empty-filtered">{t("No orders match your search")}</Card>
      ) : (
        <>
          {/* Active orders grouped by date */}
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Active Orders")} · {active.length}</p>
          {active.length === 0 && <Card className="mb-3 p-6 text-center text-sm text-ink-mute" data-testid="orders-no-active">{t("No active orders — all caught up!")}</Card>}
          {dateSections.map(([key, label]) => (
            byBucket[key].length > 0 && (
              <Section key={key} testid={`orders-section-${key}`} title={label} count={byBucket[key].length}
                tone={key === "today" ? "info" : "mute"} open={isOpen(key)} onToggle={() => toggle(key)}>
                {byBucket[key].map(row)}
              </Section>
            )
          ))}

          {/* Completed orders — collapsed by default */}
          {completed.length > 0 && (
            <Section testid="orders-section-completed" title={t("Completed Orders")} count={completed.length}
              tone="success" open={searching ? true : !collapsed.completed} onToggle={() => toggle("completed")}>
              {completed.map(row)}
            </Section>
          )}
        </>
      )}

      <OrderDetailModal orderId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
